import { useSmsAutomationAccess } from '../useSmsAutomationAccess';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { Paper, Typography, Button, Stack, Box, IconButton, Divider, Alert } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useState } from 'react';
import dayjs from 'dayjs';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { SmsPreview } from './SmsPreview';
import { useSmsPreview } from '../useSmsPreview';
import { SmsTemplateEditor } from './SmsTemplateEditor';
import { useSmsTemplates, useDeleteSmsTemplate } from '../useSmsTemplates';
import { SmsTemplateType, type SmsTemplate } from '../smsTemplates';
import Loader from '@shared/components/Loader';

const TEMPLATE_TYPE_LABELS: Record<SmsTemplateType, { id: string; defaultMessage: string }> = {
  [SmsTemplateType.DEPOSIT_REMINDER]: {
    id: 'smsTemplate.type.depositReminder',
    defaultMessage: 'Připomenutí zálohy',
  },
  [SmsTemplateType.APPOINTMENT_REMINDER]: {
    id: 'smsTemplate.type.appointmentReminder',
    defaultMessage: 'Připomenutí schůzky',
  },
  [SmsTemplateType.REVIEW_REQUEST]: {
    id: 'smsTemplate.type.reviewRequest',
    defaultMessage: 'Žádost o recenzi',
  },
  // [SmsTemplateType.APPOINTMENT_CONFIRMATION]: {
  //   id: 'smsTemplate.type.appointmentConfirmation',
  //   defaultMessage: 'Potvrzení schůzky',
  // },
  // [SmsTemplateType.CANCELLATION_NOTICE]: {
  //   id: 'smsTemplate.type.cancellationNotice',
  //   defaultMessage: 'Oznámení o zrušení',
  // },
};

export const SmsTemplateManager = () => {
  const renderPreview = useSmsPreview();
  const smsAutomationEnabled = useSmsAutomationAccess();
  const { t } = useIntlTranslations();
  const intl = useIntl();
  const [editorOpen, setEditorOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<SmsTemplate | undefined>();
  const { data, isLoading, error } = useSmsTemplates();
  const { mutate: deleteTemplate } = useDeleteSmsTemplate();

  const handleEdit = (template: SmsTemplate) => {
    setSelectedTemplate(template);
    setEditorOpen(true);
  };

  const handleCreate = () => {
    setSelectedTemplate(undefined);
    setEditorOpen(true);
  };

  const handleClose = () => {
    setEditorOpen(false);
    setSelectedTemplate(undefined);
  };

  const handleDelete = (id: string) => {
    if (
      window.confirm(
        intl.formatMessage({
          id: 'smsTemplate.deleteConfirm',
          defaultMessage: 'Opravdu chcete smazat tuto SMS šablonu?',
        }),
      )
    ) {
      deleteTemplate(id);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  if (error) {
    return (
      <Alert severity="error">
        <FormattedMessage
          id="smsTemplate.loadError"
          defaultMessage="Nepodařilo se načíst SMS šablony"
        />
      </Alert>
    );
  }

  const templates = data?.templates || [];
  const allTypes = Object.values(SmsTemplateType);
  const noAvailableTypes = templates.length >= allTypes.length;

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6">{t('myProfile.smsTemplates')}</Typography>
        <Typography variant="body2" color="text.secondary">
          {t('smsTemplate.description')}
        </Typography>
      </Box>
      <Button
        variant="contained"
        startIcon={<AddIcon />}
        onClick={handleCreate}
        size="small"
        disabled={noAvailableTypes}
      >
        {noAvailableTypes ? (
          <FormattedMessage
            id="smsTemplate.noMoreTypes"
            defaultMessage="Žádné jiné šablony už nejsou"
          />
        ) : (
          <FormattedMessage id="smsTemplate.create" defaultMessage="Přidat šablonu" />
        )}
      </Button>

      <Stack spacing={2}>
        {templates.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography>
              <FormattedMessage id="smsTemplate.noTemplates" />
            </Typography>
          </Paper>
        ) : (
          templates.map(template => (
            <Paper key={template.id} sx={{ p: 1 }}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {intl.formatMessage(TEMPLATE_TYPE_LABELS[template.type])}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      <FormattedMessage
                        id="smsTemplate.updatedAt"
                        defaultMessage="Naposledy upraveno: {date}"
                        values={{
                          date: dayjs(template.updatedAt).format('DD.MM.YYYY'),
                        }}
                      />
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <IconButton
                      size="small"
                      onClick={() => handleEdit(template)}
                      title={intl.formatMessage({
                        id: 'common.edit',
                        defaultMessage: 'Upravit',
                      })}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(template.id)}
                      title={intl.formatMessage({
                        id: 'common.delete',
                        defaultMessage: 'Smazat',
                      })}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
                {smsAutomationEnabled && (
                  <Typography
                    variant="body2"
                    color={template.automatic ? 'primary' : 'text.secondary'}
                  >
                    {template.automatic
                      ? t('smsTemplate.automaticSchedule', {
                          days: template.daysBefore,
                          time: template.sendTime,
                        })
                      : t('smsTemplate.manualSchedule')}
                  </Typography>
                )}
                <Divider />
                <SmsPreview content={renderPreview(template.content)} />
                <Typography variant="caption" color="text.secondary">
                  <FormattedMessage
                    id="smsTemplate.previewUserHint"
                    defaultMessage="Náhled s vašimi údaji. Návštěva, služba a případné chybějící údaje jsou ukázkové; při odeslání se doplní údaje klienta."
                  />
                </Typography>
              </Stack>
            </Paper>
          ))
        )}
      </Stack>

      <SmsTemplateEditor open={editorOpen} onClose={handleClose} template={selectedTemplate} />
    </Stack>
  );
};
